/**
 * Mutaciones local-first de la ESPINA de módulos (F2-A).
 *
 * Escriben en PowerSync → write-path (`db/sync.rs`) con authz autor/editor y
 * sello de versión del curso. Las COMPOSITE (`addLessonModuleMutation`,
 * `addResourceModuleMutation`) crean la entidad de detalle Y su fila
 * `course_module` — el contrato del refactor: crear una lección crea SIEMPRE
 * su módulo (kind='lesson', ref_id=lesson.id).
 */

import {
  course_module,
  course_settings,
  eq,
  lesson,
  type PowerSyncDrizzleDB,
  resource,
} from "@space/powersync";
import { nowISO } from "../../utils/date-helpers";
import { generateUUID } from "../../utils/uuid";

// ─── course_module ───────────────────────────────────────────────────────────

export interface AddCourseModuleInput {
  courseId: string;
  unitId: string;
  kind: string;
  refId?: string | null;
  position?: number;
  visibility?: "visible" | "hidden";
  config?: unknown;
  completionRule?: unknown;
}

export async function addCourseModuleMutation(
  db: PowerSyncDrizzleDB,
  input: AddCourseModuleInput,
): Promise<string> {
  const id = generateUUID();
  const now = nowISO();
  await db.insert(course_module).values({
    id,
    unit_id: input.unitId,
    course_id: input.courseId,
    kind: input.kind,
    ref_id: input.refId ?? null,
    position: input.position ?? 0,
    visibility: input.visibility ?? "visible",
    completion_rule: JSON.stringify(input.completionRule ?? {}),
    config: JSON.stringify(input.config ?? {}),
    created_at: now,
    updated_at: now,
  });
  return id;
}

export async function updateCourseModuleMutation(
  db: PowerSyncDrizzleDB,
  moduleId: string,
  updates: Partial<{
    position: number;
    visibility: "visible" | "hidden";
    config: unknown;
    completion_rule: unknown;
  }>,
): Promise<void> {
  const patch: Record<string, unknown> = { updated_at: nowISO() };
  if (updates.position !== undefined) {
    patch.position = updates.position;
  }
  if (updates.visibility !== undefined) {
    patch.visibility = updates.visibility;
  }
  if (updates.config !== undefined) {
    patch.config = JSON.stringify(updates.config);
  }
  if (updates.completion_rule !== undefined) {
    patch.completion_rule = JSON.stringify(updates.completion_rule);
  }
  await db.update(course_module).set(patch).where(eq(course_module.id, moduleId));
}

export async function deleteCourseModuleMutation(
  db: PowerSyncDrizzleDB,
  moduleId: string,
): Promise<void> {
  await db
    .update(course_module)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(course_module.id, moduleId));
}

// ─── composites: detalle + módulo ────────────────────────────────────────────

/**
 * Crea una LECCIÓN y su módulo de espina (kind='lesson', ref_id=lesson.id).
 * Sustituye a `addLessonMutation` en el authoring: el módulo es la fila que el
 * player recorre; la lección sigue siendo el detalle (bloques/XP).
 */
export async function addLessonModuleMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    title?: string;
    position?: number;
    xpReward?: number;
  },
): Promise<{ lessonId: string; moduleId: string }> {
  const lessonId = generateUUID();
  const now = nowISO();
  await db.insert(lesson).values({
    id: lessonId,
    unit_id: params.unitId,
    course_id: params.courseId,
    title: params.title?.trim() || "Nueva lección",
    position: params.position ?? 0,
    xp_reward: params.xpReward ?? 10,
    created_at: now,
    updated_at: now,
  });
  const moduleId = await addCourseModuleMutation(db, {
    courseId: params.courseId,
    unitId: params.unitId,
    kind: "lesson",
    refId: lessonId,
    position: params.position ?? 0,
  });
  return { lessonId, moduleId };
}

/**
 * Crea un RECURSO y su módulo de espina (kind='resource', ref_id=resource.id).
 */
export async function addResourceModuleMutation(
  db: PowerSyncDrizzleDB,
  params: {
    courseId: string;
    unitId: string;
    title?: string;
    kind?: "file" | "link" | "text";
    url?: string;
    body?: string;
    position?: number;
  },
): Promise<{ resourceId: string; moduleId: string }> {
  const resourceId = generateUUID();
  const now = nowISO();
  await db.insert(resource).values({
    id: resourceId,
    course_id: params.courseId,
    kind: params.kind ?? "link",
    title: params.title?.trim() || "Nuevo recurso",
    url: params.url ?? null,
    body: params.body ?? null,
    created_at: now,
    updated_at: now,
  });
  const moduleId = await addCourseModuleMutation(db, {
    courseId: params.courseId,
    unitId: params.unitId,
    kind: "resource",
    refId: resourceId,
    position: params.position ?? 0,
  });
  return { resourceId, moduleId };
}

// ─── resource (detalle) ──────────────────────────────────────────────────────

export async function updateResourceMutation(
  db: PowerSyncDrizzleDB,
  resourceId: string,
  updates: Partial<{
    kind: "file" | "link" | "text";
    title: string;
    url: string | null;
    body: string | null;
  }>,
): Promise<void> {
  await db
    .update(resource)
    .set({ ...updates, updated_at: nowISO() })
    .where(eq(resource.id, resourceId));
}

export async function deleteResourceMutation(
  db: PowerSyncDrizzleDB,
  resourceId: string,
): Promise<void> {
  await db
    .update(resource)
    .set({ deleted_at: nowISO(), updated_at: nowISO() })
    .where(eq(resource.id, resourceId));
}

// ─── course_settings ─────────────────────────────────────────────────────────

/**
 * Upsert de los ajustes por curso (id = course_id). PowerSync no admite UPSERT
 * sobre vistas → UPDATE-o-INSERT manual (mismo patrón que lesson_progress).
 */
export async function upsertCourseSettingsMutation(
  db: PowerSyncDrizzleDB,
  courseId: string,
  updates: Partial<{
    grading_scheme: string | null;
    grading_model_id: string | null;
    enabled_module_kinds: string[] | null;
    meeting_provider: string | null;
    completion_config: unknown;
    forum_policy: unknown;
    features: unknown;
    media_settings: unknown;
  }>,
): Promise<void> {
  const now = nowISO();
  const patch: Record<string, unknown> = { updated_at: now };
  if (updates.grading_scheme !== undefined) {
    patch.grading_scheme = updates.grading_scheme;
  }
  if (updates.grading_model_id !== undefined) {
    patch.grading_model_id = updates.grading_model_id;
  }
  if (updates.enabled_module_kinds !== undefined) {
    patch.enabled_module_kinds =
      updates.enabled_module_kinds === null ? null : JSON.stringify(updates.enabled_module_kinds);
  }
  if (updates.meeting_provider !== undefined) {
    patch.meeting_provider = updates.meeting_provider;
  }
  if (updates.completion_config !== undefined) {
    patch.completion_config = JSON.stringify(updates.completion_config ?? {});
  }
  if (updates.forum_policy !== undefined) {
    patch.forum_policy = JSON.stringify(updates.forum_policy ?? {});
  }
  if (updates.features !== undefined) {
    patch.features = JSON.stringify(updates.features ?? {});
  }
  if (updates.media_settings !== undefined) {
    patch.media_settings = JSON.stringify(updates.media_settings ?? {});
  }

  const existing = await db
    .select({ id: course_settings.id })
    .from(course_settings)
    .where(eq(course_settings.id, courseId))
    .limit(1);
  if (existing[0]) {
    await db.update(course_settings).set(patch).where(eq(course_settings.id, courseId));
  } else {
    await db.insert(course_settings).values({ id: courseId, created_at: now, ...patch });
  }
}
